import { ActionData, Privacy } from '@bonusly/common-action-input';
import { ReasonToken } from '@bonusly/recipient-entities';

// common fields to bonus, comments and awards creation / update
interface CreateBonusData {
	reason: string;
	privacy: Privacy;
	attachments_attributes?: Record<string, any>;
	reason_tokens?: ReasonToken[];
}

// specific fields to comment creation / update
interface CreateCommentData extends CreateBonusData {
	include_parent_giver: boolean;
	parent_bonus_id: string;
}

// specific fields to award creation / update
interface CreateAwardData extends CreateBonusData {
	award_id: string;
	giver_id: string;
	hide_giver: boolean;
}

interface Props {
	action: ActionData;
	selfUserId?: string;
	bonus?: { id: string };
	award?: {
		id: string;
		reason?: string;
		giver: { id: string };
		hide_giver?: boolean;
	};
}

// templated awards username key to be replaced by the recipients usernames
const LEGACY_USERNAME_KEY = '@receiver_username'; // See BonusTemplate::LEGACY_PLACEHOLDER_RECIPIENT
const USERNAME_KEY = '@recipient'; // See BonusTemplate::PLACEHOLDER_RECIPIENT

export function createBonusData({ action, bonus, award }: Props) {
	// return can be data for bonus, comment or award

	const data: CreateBonusData = {
		privacy: action.privacy,
		reason: action.text,
	};

	if (action.attachment) {
		data.attachments_attributes = [action.attachment.toJSON()];
	}

	const recipients = action.recipients?.filter((r) => !!r.id) || [];

	data.reason_tokens = recipients.map(({ id, dimension, displayName, type = 'username', username, userIds }) => ({
		context: 'recipient',
		token: `@${username}`,
		display_name: displayName,
		id: type === 'group' ? id : undefined,
		type: (type === 'group' ? dimension : type) as ReasonToken['type'],
		user_ids: type === 'group' ? userIds : [id],
	}));

	// if has bonus.id it is a comment
	if (bonus && bonus.id) {
		const commentData = data as CreateCommentData;
		commentData.include_parent_giver = !!action.includeGiver;
		commentData.parent_bonus_id = bonus.id;

		// just in case it is a comment with an award too, early return
		return commentData;
	}

	if (award && award.id) {
		const awardData = data as CreateAwardData;
		awardData.award_id = award.id;
		awardData.giver_id = award.giver?.id;
		awardData.hide_giver = !!award.hide_giver;

		// award.reason exists for templated awards
		// reason will be the template with the usernames replaced + the custom message without the usernames
		if (award.reason) {
			const users = action.recipients?.map((r) => `@${r.username}`) || [];
			const usersRegexp = new RegExp(`[ ,]?(${users.join('|')})[ ,]?`, 'g');
			let reason = String(award.reason || '');
			reason = reason.replace(LEGACY_USERNAME_KEY, 'USERS_PLACEHOLDER');
			reason = reason.replace(USERNAME_KEY, 'USERS_PLACEHOLDER');
			reason = reason.replace('USERS_PLACEHOLDER', users.join(' '));
			awardData.reason = `${reason} ${action.text.replace(usersRegexp, '')}`;
		}

		return awardData;
	}

	return data;
}
